import React from 'react';
import moment from 'moment';
import { computed, observable, reaction } from 'mobx';
import Jed, { JedOptions, Jedable } from 'jed';
import * as UserService from 'services/accounts/user_service';
import { GlobalAccountsDomain } from 'services/accounts/accounts_domain';
// These are necessary because of https://create-react-app.dev/docs/troubleshooting/#momentjs-locales-are-missing
import 'moment/locale/ja';
import 'moment/locale/zh-cn';
import 'moment/locale/zh-tw';
import enUS from './localization/messages-en_US.json';
import jaJP from './localization/messages-ja_JP.json';
// import zhCN from './localization/messages-zh_CN.json';
import zhTW from './localization/messages-zh_TW.json';

// See this reference for this wonderful trick/hack to automatically generate typeguards for string union types
// https://stackoverflow.com/questions/36836011/checking-validity-of-string-literal-union-type-at-runtime
// This was then modified/abused to create LocaleRuntimeCreator

type ILocaleData<LocaleType extends string> = {
  code: LocaleType;
  label: string;
  options: JedOptions;
};

export const LocaleRuntimeCreator = <LocaleType extends string>(...values: ILocaleData<LocaleType>[]) => {
  Object.freeze(values);
  const map = {} as Record<LocaleType, ILocaleData<LocaleType>>;
  values.forEach((value) => {
    map[value.code] = value;
  });

  const valueSet: Set<string> = new Set(values.map((v) => v.code));

  const guard = (value: string): value is LocaleType => {
    return valueSet.has(value);
  };

  const check = (value: string): LocaleType => {
    if (!guard(value)) {
      const actual = JSON.stringify(value);
      const expected = values.map(s => JSON.stringify(s)).join(' | ');
      throw new TypeError(`Value '${actual}' is not assignable to type '${expected}'.`);
    }
    return value;
  };

  const runtime = {
    values,
    map,
    guard,
    check,
  };

  return Object.freeze(runtime as typeof runtime & { code: LocaleType; data: ILocaleData<LocaleType> });
};

const LocaleRuntime = LocaleRuntimeCreator(
  {
    code: 'en_US',
    label: 'English',
    options: enUS,
  },
  {
    code: 'ja_JP',
    label: '日本語',
    options: jaJP,
  },
  // {
  //   code: 'zh_CN',
  //   label: '简体中文',
  //   options: zhCN,
  // },
  {
    code: 'zh_TW',
    label: '繁體中文',
    options: zhTW,
  },
);

export type Locale = typeof LocaleRuntime.code;
export type LocaleData = typeof LocaleRuntime.data;
export const LOCALE_LIST = LocaleRuntime.values;
export const LOCALE_MAP = LocaleRuntime.map;
const STORAGE_LOCALE_KEY = 'locale';

export const GlobalLocaleStore = observable.box(getPersistedLocale());
export const LocaleContext = React.createContext(GlobalLocaleStore.get());

export function getPersistedLocale(): Locale {
  const locale = localStorage.getItem(STORAGE_LOCALE_KEY);
  if (locale == null || !LocaleRuntime.guard(locale)) {
    return 'en_US';
  }
  return locale;
}

export function setPersistedLocale(locale: Locale) {
  localStorage.setItem(STORAGE_LOCALE_KEY, locale);
}

export function localeToLanguageCode(locale: Locale) {
  return locale.toLowerCase().replace(/_/g, '-');
}

reaction(
  () => GlobalLocaleStore.get(),
  (locale) => {
    moment.locale(localeToLanguageCode(locale));
  },
  { fireImmediately: true },
);

reaction(
  () => GlobalLocaleStore.get(),
  (locale) => {
    setPersistedLocale(locale);
    if (GlobalAccountsDomain.user != null) {
      UserService.patch({
        domain: GlobalAccountsDomain,
        preferredLanguage: localeToLanguageCode(GlobalLocaleStore.get()),
      });
    }
  },
);

class JedProxy implements Jedable {
  jeds: Record<Locale, Jed>;

  constructor() {
    this.jeds = {} as Record<Locale, Jed>;
    LOCALE_LIST.forEach((data) => {
      this.jeds[data.code] = new Jed(data.options);
    });
  }

  @computed
  get activeJed(): Jed {
    return this.jeds[GlobalLocaleStore.get()];
  }

  pgettext(context: string, msgid: string): string {
    return this.activeJed.pgettext(context, msgid);
  }

  npgettext(context: string, singularKey: string, pluralKey: string, value: number): string {
    return this.activeJed.npgettext(context, singularKey, pluralKey, value);
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  sprintf(format: string, ...args: any[]): string {
    return this.activeJed.sprintf(format, ...args);
  }

  numberFormat(x: number): string {
    return x.toLocaleString(localeToLanguageCode(GlobalLocaleStore.get()));
  }

  percentFormat(x: number, decimals: number): string {
    if (isNaN(x)) {
      return '0%';
    }
    const exp = Math.pow(10, decimals);
    const value = Math.round(x * 100 * exp) / 100;
    return `${value.toLocaleString(localeToLanguageCode(GlobalLocaleStore.get()))}%`;
  }
}

const i18n = new JedProxy();

export default i18n;
